import React, { useState, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import RequestForm from './RequestForm';
import Chatbot from './Chatbot';
import ServiceAreaMap from './ServiceAreaMap';
import jorgePhoto from '../assets/firefly1.png';
import heroBg from '../assets/Firefly2.png';
import teamBg from '../assets/Firefly3.png';
import aboutBg from '../assets/firefly4.png';
import './LandingPage.css';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.25, 0.1, 0.25, 1] } }
};

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } }
};

const LandingPage = () => {
  const [selectedService, setSelectedService] = useState('');
  const [modalService, setModalService] = useState(null);
  const [selectedMaterials, setSelectedMaterials] = useState({});
  const heroRef = useRef(null);
  const aboutRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const { scrollYProgress: aboutScrollProgress } = useScroll({
    target: aboutRef,
    offset: ['start end', 'end start'],
  });
  // Hero background parallax: slower downward drift + subtle zoom + fade out
  const heroBgY = useTransform(scrollYProgress, [0, 1], ['0%', '35%']);
  const heroBgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.25]);
  const heroBgOpacity = useTransform(scrollYProgress, [0, 0.8, 1], [1, 0.85, 0.4]);
  // About background parallax
  const aboutBgY = useTransform(aboutScrollProgress, [0, 1], ['0%', '30%']);
  const aboutBgScale = useTransform(aboutScrollProgress, [0, 1], [1.05, 1.2]);
  const aboutBgOpacity = useTransform(aboutScrollProgress, [0, 0.3, 0.7, 1], [0.4, 0.8, 0.8, 0.4]);

  const services = [
    
    {
      num: '01',
      title: 'Cable Management',
      startingPrice: 50,
      desc: `Tangled mess behind the desk or TV? We clean it up, routed, labeled, hidden, so everything looks intentional and stays easy to service.`,
      materials: [
        { name: 'Cable Raceway Kit (6ft)', price: 15 },
        { name: 'Velcro Cable Ties (pack)', price: 6 },
        { name: 'Cable Sleeve / Wrap', price: 10 },
        { name: 'Under-Desk Cable Tray', price: 20 },
        { name: 'Adhesive Cable Clips (pack)', price: 5 },
      ]
    },
    {
      num: '02',
      title: 'Network Setup',
      startingPrice: 75,
      desc: `WiFi that actually reaches your bedroom. Wired connections that don't drop. Networks that work.`,
      materials: [
        { name: 'Cat6 Ethernet Cable (25ft)', price: 15 },
        { name: 'Cat6 Ethernet Cable (50ft)', price: 22 },
        { name: 'Cable Management Clips', price: 8 },
        { name: 'Ethernet Wall Plate', price: 10 },
        { name: 'Surge Protector (6-outlet)', price: 20 },
      ]
    },
    {
      num: '03',
      title: 'Tech and TV Mounting',
      startingPrice: 80,
      desc: `Wall-mounting your TV or setting up a new device? We'll handle the install cleanly and make sure everything's positioned and connected just right.`,
      materials: [
        { name: 'TV Wall Mount Bracket', price: 25 },
        { name: 'Cable Hider / Raceway Kit', price: 15 },
        { name: 'Surge-Protected Extension Cord (6ft)', price: 18 },
        { name: 'HDMI Cable (6ft)', price: 12 },
        { name: 'Cable Clips & Zip Ties', price: 5 },
      ]
    },
    {
      num: '04',
      title: 'System Setup',
      startingPrice: 40,
      desc: `New computer, phone, or tablet? We'll get everything configured and show you how it works.`
    },


    {
      num: '05',
      title: 'Cloud or Disk Backup',
      startingPrice: 45,
      desc: `We set up automatic cloud backups or local disk backups so your files are always protected and help you recover data when things go wrong.`,
      materials: [
        { name: 'External Hard Drive (1TB)', price: 65 },
        { name: 'External Hard Drive (2TB)', price: 90 },
        { name: 'USB Flash Drive (64GB)', price: 15 },
        { name: 'Cloud Backup Setup (1 year)', price: 30 },
      ]
    },


    {
      num: '06',
      title: 'Tech Support',
      startingPrice: 30,
      desc: `When you just need someone who speaks both tech and human to help you figure something out.`
    }
  ];

  const stats = [
    { value: 'Same-Day', label: 'Response' },
    { value: 'Flat-Rate', label: 'Pricing' },
    { value: 'In-Home', label: 'Service' },
    { value: 'No Hidden', label: 'Fees' },
  ];

  const smoothScrollTo = (e, targetId) => {
    e.preventDefault();
    const el = document.getElementById(targetId);
    if (!el) return;
    const navHeight = 70;
    const top = el.getBoundingClientRect().top + window.scrollY - navHeight;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const scrollToForm = (serviceName) => {
    setSelectedService(serviceName);
    const formSection = document.getElementById('contact');
    if (!formSection) return;
    const navHeight = 70;
    const top = formSection.getBoundingClientRect().top + window.scrollY - navHeight;
    window.scrollTo({ top, behavior: 'smooth' });
  };

  const handleServiceClick = (service) => {
    if (service.materials) {
      setModalService(service);
      setSelectedMaterials({});
    }
  };

  const toggleMaterial = (name) => {
    setSelectedMaterials(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const materialsTotal = modalService
    ? modalService.materials
        .filter(m => selectedMaterials[m.name])
        .reduce((sum, m) => sum + m.price, 0)
    : 0;

  const handleRequestService = () => {
    const service = modalService;
    setModalService(null);
    scrollToForm(service.title);
  };

  return (
    <div className="landing-page">

      {/* Navigation */}
      <motion.nav
        className="nav"
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <div className="nav-logo">MTS <span>|</span> Mobile Tech Solutions</div>
        <div className="nav-links">
          <a href="#services" onClick={(e) => smoothScrollTo(e, 'services')}>Services</a>
          <a href="#team" onClick={(e) => smoothScrollTo(e, 'team')}>Who We Are</a>
          <a href="#about" onClick={(e) => smoothScrollTo(e, 'about')}>About</a>
          <a href="#service-area" onClick={(e) => smoothScrollTo(e, 'service-area')}>Service Area</a>
          <a href="#contact" onClick={(e) => smoothScrollTo(e, 'contact')}>Get Help</a>
        </div>
      </motion.nav>

      {/* Hero */}
      <section className="hero" ref={heroRef}>
        {/* Parallax background image */}
        <motion.div
          className="hero-bg"
          style={{
            backgroundImage: `url(${heroBg})`,
            y: heroBgY,
            scale: heroBgScale,
            opacity: heroBgOpacity,
          }}
          aria-hidden="true"
        />
        <div className="hero-bg-overlay" aria-hidden="true" />

        <motion.div
          className="hero-left"
          variants={stagger}
          initial="hidden"
          animate="show"
        >
          <motion.div variants={fadeUp} className="hero-eyebrow">
            <span className="eyebrow-dot" /> Mobile tech support 
          </motion.div>
          <motion.h1 variants={fadeUp}>
            Your tech problems<br />
            <em>solved today.</em>
          </motion.h1>
          <motion.p variants={fadeUp} className="hero-sub">
            We're here to help with all the things your tech should do. Honest, in-home support without the upsell.
          </motion.p>
          <motion.div variants={fadeUp} className="hero-cta">
            <button onClick={() => scrollToForm('')} className="btn-primary">
              Request Help
            </button>
            <a href="#services" onClick={(e) => smoothScrollTo(e, 'services')} className="btn-ghost">See what we do →</a>
          </motion.div>
          <motion.div variants={fadeUp} className="hero-stats">
            {stats.map((stat, i) => (
              <React.Fragment key={i}>
                <div className="hero-stat">
                  <span className="hero-stat-value">{stat.value}</span>
                  <span className="hero-stat-label">{stat.label}</span>
                </div>
                {i < stats.length - 1 && <div className="hero-stat-divider" />}
              </React.Fragment>
            ))}
          </motion.div>
        </motion.div>

      </section>

      {/* Trust Badges */}
      <section className="trust" aria-label="Trust and security">
        <div className="trust-inner">
          <div className="trust-label">Trusted &amp; Verified</div>
          <div className="trust-badges">
            <a href="https://trustlock.co" className="trust-badge" target="_blank" rel="noopener noreferrer">
              <img src="https://trustlock.co/wp-content/uploads/2019/01/money-back-guarantee-trust-badge.png" alt="Money Back Guarantee Trust Badge" width="171" height="75" />
            </a>
            <a href="https://trustlock.co" className="trust-badge" target="_blank" rel="noopener noreferrer">
              <img alt="Trust Badges" style={{ border: 0 }} src="https://trustlock.co/wp-content/uploads/2019/01/satisisfaction-guaranteed-badge-iamge-icon-badge.png" width="171" height="75" />
            </a>
            <a href="https://trustlock.co" className="trust-badge" target="_blank" rel="noopener noreferrer">
              <img alt="Trust Badges" style={{ border: 0 }} src="https://trustlock.co/wp-content/uploads/2019/01/free-trust-badgesBEST-PRICE-GUARANTEED.png" width="238" height="44" />
            </a>
          </div>
        </div>
      </section>

      {/* Who We Are */}
      <section id="team" className="team" style={{ backgroundImage: `url(${teamBg})` }}>
        <motion.div
          className="team-header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="section-label">Who we are</div>
          <h2 className="team-heading">
            Real person.<br /><em>Reliable service.</em>
          </h2>
          <p className="team-sub">
            One Technician, One Stop.
          </p>
        </motion.div>

        <motion.div
          className="team-card-solo"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-30px' }}
          transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="team-photo-wrap">
            <img src={jorgePhoto} alt="Jorge Reyes" className="team-photo-img" />
          </div>
          <div className="team-info">
            <div className="team-name">Jorge Reyes</div>
            <div className="team-role">Founder &amp; Technician</div>
            <p className="team-quote">
              " I started MTS because I was tired of watching people get overcharged for simple tech fixes or TV mounts.
          When you call, you get me. I show up, I fix it, and I make sure you understand what happened. No runaround, no hand-offs.
          You can have the confidence that the job will get done right the first time, and the respect of knowing you're not being taken advantage of."
            </p>
          </div>
        </motion.div>
      </section>

      {/* Services */}
      <section id="services" className="services">
        <motion.div
          className="section-label"
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
        >
          What we do
        </motion.div>
        <motion.p
          className="services-sub"
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45, delay: 0.1 }}
        >
          Services we provide.
        </motion.p>

        <div className="services-grid">
          {services.map((service, index) => (
            <motion.div
              key={index}
              className={`service-item${service.materials ? ' has-materials' : ''}`}
              onClick={() => handleServiceClick(service)}
              initial="rest"
              whileInView="visible"
              whileHover="hover"
              viewport={{ once: true, margin: '-30px' }}
              variants={{
                rest: { opacity: 0, y: 40 },
                visible: {
                  opacity: 1,
                  y: 0,
                  transition: { duration: 0.6, delay: (index % 2) * 0.1, ease: [0.25, 0.1, 0.25, 1] }
                },
                hover: {
                  y: -6,
                  transition: { type: 'spring', stiffness: 300, damping: 20 }
                }
              }}
            >
              {service.startingPrice && (
                <motion.div
                  className="service-price"
                  variants={{
                    rest: { scale: 1 },
                    visible: { scale: 1 },
                    hover: {
                      scale: 1.08,
                      boxShadow: '0 6px 24px rgba(220, 38, 38, 0.45)',
                      transition: { type: 'spring', stiffness: 400, damping: 15 }
                    }
                  }}
                >
                  <span className="service-price-label">Starting at</span>
                  <span className="service-price-value">${service.startingPrice}</span>
                </motion.div>
              )}
              <motion.div
                className="service-num"
                variants={{
                  rest: {},
                  visible: {},
                  hover: {
                    x: 4,
                    transition: { type: 'spring', stiffness: 300, damping: 20 }
                  }
                }}
              >
                {service.num}
              </motion.div>
              <h3 className="service-title">{service.title}</h3>
              <p className="service-desc">{service.desc}</p>
              {service.materials && (
                <div className="service-materials-hint">Materials available, click to view</div>
              )}
              <div className="service-arrow-wrap">
                <span className="service-arrow-note">Request this service</span>
                <button
                  className="service-arrow"
                  onClick={e => { e.stopPropagation(); scrollToForm(service.title); }}
                >→</button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Materials Modal */}
        <AnimatePresence>
          {modalService && (
            <motion.div
              className="modal-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setModalService(null)}
            >
              <motion.div
                className="modal"
                initial={{ opacity: 0, scale: 0.93, y: 28 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.93, y: 28 }}
                transition={{ type: 'spring', stiffness: 320, damping: 28 }}
                onClick={e => e.stopPropagation()}
              >
                <button className="modal-close" onClick={() => setModalService(null)}>✕</button>
                <div className="modal-header">
                  <div className="modal-service-num">{modalService.num}</div>
                  <h3 className="modal-title">{modalService.title}</h3>
                  <p className="modal-subtitle">Select any materials you'd like included. Prices shown are estimates.</p>
                </div>

                <div className="modal-materials">
                  {modalService.materials.map((material, i) => (
                    <motion.label
                      key={material.name}
                      className="material-row"
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.06 + 0.1, duration: 0.3 }}
                    >
                      <div className="material-check-wrap">
                        <input
                          type="checkbox"
                          checked={!!selectedMaterials[material.name]}
                          onChange={() => toggleMaterial(material.name)}
                          className="material-checkbox"
                        />
                        <span className="material-name">{material.name}</span>
                      </div>
                      <span className="material-price">${material.price}</span>
                    </motion.label>
                  ))}
                </div>

                <div className="modal-total-row">
                  <span className="modal-total-label">Estimated Materials Total</span>
                  <motion.span
                    className="modal-total-value"
                    key={materialsTotal}
                    initial={{ scale: 1.15, color: '#DC2626' }}
                    animate={{ scale: 1, color: '#ffffff' }}
                    transition={{ duration: 0.25 }}
                  >
                    ${materialsTotal}
                  </motion.span>
                </div>

                <button className="modal-request-btn" onClick={handleRequestService}>
                  Request This Service →
                </button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>

      {/* About */}
      <section id="about" className="about" ref={aboutRef}>
        <motion.div
          className="about-bg"
          style={{
            backgroundImage: `url(${aboutBg})`,
            y: aboutBgY,
            scale: aboutBgScale,
            opacity: aboutBgOpacity,
          }}
          aria-hidden="true"
        />
        <div className="about-bg-overlay" aria-hidden="true" />
        <motion.div
          className="about-left"
          initial={{ opacity: 0, x: -24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="section-label">How we work</div>
          <h2>
            <em>No stress.</em><br />
            No pressure.<br />
            Just help.
          </h2>
        </motion.div>
        <motion.div
          className="about-right"
          initial={{ opacity: 0, x: 24 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.15, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <p>
            We're not trying to sell you a service plan or make you feel
            dumb for asking questions. Tech breaks. It's confusing. We get it.
          </p>
          <p>
            You tell us what's wrong, we'll figure out what's actually wrong,
            fix it, and explain what happened in plain English. That's the deal.
          </p>
          <motion.ul
            className="values-list"
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {[
              'We answer our phone',
              'We show up when we say we will',
              'We explain things without the hassle',
              "We don't charge you for things you don't need",
            ].map((item, i) => (
              <motion.li key={i} variants={fadeUp}>{item}</motion.li>
            ))}
          </motion.ul>
        </motion.div>
      </section>

      {/* Service Area Map */}
      <ServiceAreaMap />

      {/* Contact & Form */}
      <motion.section
        id="contact"
        className="contact"
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
      >
        <div className="contact-left">
          <h2>Let's fix it.</h2>
          <p className="contact-intro">
            Fill out the form and we'll get back to you same-day.
            Or just call if that's easier.
          </p>
          <div className="contact-info">
            <div className="contact-row">
              <div className="contact-label">Email</div>
              <div className="contact-val">
                <a href="mailto:jorge@mtstechsolutions.com">jorge@mtstechsolutions.com</a>
              </div>
            </div>
            <div className="contact-row">
              <div className="contact-label">Hours</div>
              <div className="contact-val">Sunday: CLOSED</div>
              <div className="contact-val">Monday: 9am–7pm</div>
              <div className="contact-val">Tuesday: 9am–7pm</div>
              <div className="contact-val">Wednesday: 9am–7pm</div>
              <div className="contact-val">Thursday: 9am–7pm</div>
              <div className="contact-val">Friday: 9am–5pm</div>
              <div className="contact-val">Saturday: 10am–3pm</div>
            </div>
          </div>
        </div>
        <div className="contact-right">
          <RequestForm preSelectedService={selectedService} />
        </div>
      </motion.section>

      {/* Chatbot */}
      <Chatbot />

      {/* Footer */}
      <footer className="footer">
        <div className="footer-top">
          <div className="footer-brand">
            <div className="footer-logo">MTS <span>|</span> Mobile Tech Solutions</div>
            <p className="footer-tag">Mobile tech support and honest help, right at your door</p>
          </div>

          <div className="footer-col">
            <div className="footer-col-label">Explore</div>
            <a href="#services">Services</a>
            <a href="#about">About</a>
            <a href="#contact">Get Help</a>
          </div>

          <div className="footer-col">
            <div className="footer-col-label">Contact</div>
            <a href="mailto:jorge@mtstechsolutions.com">jorge@mtstechsolutions.com</a>
            <span className="footer-meta">Mon–Sat · Same-day response</span>
          </div>
        </div>

        <div className="footer-bottom">
          <div className="footer-copy">© 2026 MTS Tech Solutions</div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
